import { useCallback, useMemo, useState } from "react";

import {
  OnWorkspaceBalance,
  OnWorkspaceBalanceSubscriptionResult,
} from "@/graphql/billing.graphql";

import useBasicData from "./useBasicData";
import useOnValueChanged from "./useOnValueChanged";
import { useSubscription } from "./useSubscription";

export interface WorkspaceEntitlementData {
  balance: number | null;
  hasAccess: boolean;
  overage: number | null;
  usage: number | null;
  reservedUnits: number;
  availableBalance: number | null;
  isSoftLimit: boolean;
  isStale: boolean;
  observedAt: string | null;
}

export const useWorkspaceEntitlement = () => {
  const { activeWorkspace } = useBasicData();
  const activeWorkspaceId = activeWorkspace?.id;

  const [entitlement, setEntitlement] =
    useState<WorkspaceEntitlementData | null>(null);

  const variables = useMemo(
    () => ({
      input: {
        workspaceId: activeWorkspaceId,
      },
    }),
    [activeWorkspaceId],
  );

  useOnValueChanged(activeWorkspaceId, () => {
    setEntitlement(null);
  });

  const handleSubscription = useCallback(
    (_previousResult: void, value: OnWorkspaceBalanceSubscriptionResult) => {
      const data = value.onWorkspaceBalance;
      if (data) {
        setEntitlement({
          balance: data.balance ?? null,
          hasAccess: data.hasAccess,
          overage: data.overage ?? null,
          usage: data.usage ?? null,
          reservedUnits: data.reservedUnits,
          availableBalance: data.availableBalance ?? null,
          isSoftLimit: data.isSoftLimit,
          isStale: data.isStale,
          observedAt: data.observedAt ?? null,
        });
      }
    },
    [],
  );

  const [subscriptionResult] = useSubscription(
    {
      query: OnWorkspaceBalance,
      variables,
      pause: !activeWorkspaceId,
    },
    handleSubscription,
  );

  return useMemo(
    () => ({
      entitlement,
      loading: !entitlement && !subscriptionResult.error,
    }),
    [entitlement, subscriptionResult.error],
  );
};
